import { useState, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { useStepUp } from '../lib/auth/useStepUp'
import { useAuth } from '../lib/auth/useAuth'
import { Button } from '@/components/ui/button'

interface StepUpButtonProps {
  scope: string
  onAction: () => Promise<void>
  onSuccess?: () => void
  onError?: (error: string) => void
  variant?: 'primary' | 'ghost' | 'danger'
  size?: 'default' | 'sm'
  disabled?: boolean
  children: React.ReactNode
}

export function StepUpButton({
  scope,
  onAction,
  onSuccess,
  onError,
  variant = 'primary',
  size = 'default',
  disabled = false,
  children,
}: StepUpButtonProps) {
  const { stepUpPolicy } = useAuth()
  const { performStepUp, reset } = useStepUp()
  const [phase, setPhase] = useState<'idle' | 'step-up' | 'action'>('idle')
  const [error, setError] = useState<string | null>(null)

  const scopeRequired = stepUpPolicy[scope] === true
  const isPending = phase !== 'idle'

  const handleClick = useCallback(async () => {
    setError(null)

    if (scopeRequired) {
      setPhase('step-up')
      try {
        await performStepUp()
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Step-up verification failed'
        setError(msg)
        onError?.(msg)
        setPhase('idle')
        return
      }
    }

    setPhase('action')
    try {
      await onAction()
      onSuccess?.()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Action failed'
      setError(msg)
      onError?.(msg)
    } finally {
      setPhase('idle')
      reset()
    }
  }, [scopeRequired, performStepUp, onAction, onSuccess, onError, reset])

  const btnVariant =
    variant === 'danger' ? 'destructive' : variant === 'ghost' ? 'ghost' : 'default'
  const btnSize = size === 'sm' ? 'sm' : 'default'

  return (
    <div className="inline-flex items-center gap-2">
      <Button
        onClick={handleClick}
        disabled={disabled || isPending}
        variant={btnVariant}
        size={btnSize}
      >
        {phase === 'step-up' ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Verifying...
          </>
        ) : phase === 'action' ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {children}
          </>
        ) : (
          children
        )}
      </Button>
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  )
}
